/**
 * Handoff protocol v1 — the public, versioned wire envelope that lets a third
 * party ship an "Inspect in MIME Smith" button without us writing any
 * per-integration code.
 *
 * The privacy guarantee is preserved end to end: a vendor hands us *their*
 * bytes, in *their* user's browser, and *our* parser runs locally. Nothing
 * leaves the device. The fragment carries this for free, since a URL hash is
 * never sent to a server by any browser.
 *
 * Two envelope shapes are defined here. Only one has a receiver:
 *
 *  1. URL fragment — WIRED. The vendor links to `#import=…` with the message
 *     gzip+base64url-packed into the hash. Encoded by
 *     {@link encodeHandoffFragment}, decoded by {@link decodeHandoffFragment},
 *     consumed by `useHandoffImport`.
 *  2. postMessage — NOT WIRED. {@link HandoffMessage} and its validator
 *     {@link isHandoffMessage} exist, but nothing in `src/` adds a `message`
 *     listener and there is no origin allowlist anywhere in the tree, so a
 *     vendor posting one is posting into a window that is not listening.
 *     Anyone finishing this must write the origin gate FIRST: a listener that
 *     accepts an envelope from any origin lets a hostile page feed the
 *     analyst a message and read the result. Do not treat
 *     {@link isHandoffMessage} as that gate; it validates SHAPE, and an
 *     attacker's envelope is as well-formed as a partner's.
 *
 * COMPRESSION REUSE: the fragment payload is the *exact* same gzip+base64url
 * scheme as the shareable permalink (`src/utils/permalink.ts`). We call
 * `encodePermalink` / `decodePermalinkDetailed` so there is a single
 * compression implementation, a single 512 KiB compressed cap, and a single
 * 8 MiB zip-bomb decompression guard. The handoff fragment simply *re-frames*
 * the permalink payload under the `#import=` namespace with the protocol
 * version + an optional `source` / `return` envelope so an import link reads
 * as a deliberate cross-app handoff, not a "share this view" permalink.
 *
 * No network call is ever issued here — gzip / gunzip run via the browser's
 * `CompressionStream` (see permalink.ts).
 */

import {
  decodePermalinkDetailed,
  encodePermalink,
  type PermalinkDecodeResult,
} from "../utils/permalink";

/** Protocol version. Bumped only on a breaking wire-shape change. */
export const HANDOFF_VERSION = 1 as const;

/** The single fragment namespace an import link lives under: `#import=1&…`. */
const FRAGMENT_PREFIX = `#import=${HANDOFF_VERSION}`;

/**
 * The v1 wire envelope.
 *
 * `mime` carries the raw RFC 5322 message. It is typed `string | ArrayBuffer`
 * so a vendor can post either the decoded source text *or* the raw bytes over
 * postMessage (an `ArrayBuffer` is the structured-clone-friendly way to ship
 * an exact byte buffer without a base64 round trip). The URL-fragment
 * transport always carries a `string` (the gz-b64 payload decompresses to the
 * source text); {@link normalizeHandoffMime} collapses either form to a string
 * for the parser.
 *
 * `sourceId` is an opaque vendor identifier (e.g. `"acme-helpdesk"`) used to
 * render "Inspecting message from <source>…". `return` is an absolute URL the
 * "Back to <source>" affordance links to. Both are optional.
 */
export interface HandoffMessage {
  v: typeof HANDOFF_VERSION;
  kind: "import-eml";
  mime: string | ArrayBuffer;
  sourceId?: string;
  return?: string;
}

/** Decoded fragment payload (after gunzip). `mime` is always the source text. */
export interface DecodedHandoff {
  mime: string;
  sourceId?: string;
  return?: string;
}

/**
 * Opaque vendor source id. Same URL-safe alphabet as the permalink evidence /
 * tab segments so it survives a hash round trip without percent-encoding.
 * `1..64` chars keeps a hand-mangled fragment from smuggling a huge token.
 */
const SOURCE_ID_RE = /^[A-Za-z0-9._-]{1,64}$/;

/**
 * Coerce a {@link HandoffMessage.mime} (string or ArrayBuffer) to the source
 * text the parser consumes.
 *
 * IMPORTANT byte-faithfulness note: an `ArrayBuffer` is decoded with the
 * byte-per-code-unit ("latin1") mapping rather than `TextDecoder("utf-8")`.
 * The whole codebase relies on `charCodeAt(i) === octet(i)` for raw message
 * source (see `src/utils/buffer.ts`), so a vendor shipping a non-ASCII body as
 * bytes keeps its exact octets for the DKIM / S-MIME body-hash recompute. A
 * UTF-8 decode here would re-encode 0x80–0xFF and corrupt the hash.
 */
export function normalizeHandoffMime(mime: string | ArrayBuffer): string {
  if (typeof mime === "string") return mime;
  const bytes = new Uint8Array(mime);
  let out = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const slice = bytes.subarray(i, Math.min(i + chunk, bytes.length));
    out += String.fromCharCode.apply(null, Array.from(slice) as unknown as number[]);
  }
  return out;
}

/**
 * Build a cross-origin-safe import fragment for a message.
 *
 * Reuses the permalink gz-b64 codec: `encodePermalink(mime)` returns
 * `#eml=1:<b64>`, and we lift the `<b64>` payload out and re-frame it under
 * `#import=1&eml=<b64>` so the link is unambiguously an import handoff. The
 * optional `sourceId` / `return` ride along as URL-safe `&` segments.
 *
 * Throws if the compressed payload exceeds the permalink's 512 KiB cap
 * (surfaced by `encodePermalink`) — the caller decides whether to fall back to
 * postMessage / file drop for an over-cap message.
 */
export async function encodeHandoffFragment(
  mime: string | ArrayBuffer,
  options?: { sourceId?: string; return?: string },
): Promise<string> {
  const source = normalizeHandoffMime(mime);
  const { hash } = await encodePermalink(source);
  // `hash` is `#eml=1:<b64>` (no &tab/&filters suffix because we pass none).
  // Lift the raw gz-b64 payload so we can re-frame it under #import=.
  const eml = hash.slice("#eml=1:".length);
  let fragment = `${FRAGMENT_PREFIX}&eml=${eml}`;
  const sourceId = options?.sourceId?.trim();
  if (sourceId && SOURCE_ID_RE.test(sourceId)) {
    fragment += `&source=${sourceId}`;
  }
  const ret = options?.return?.trim();
  if (ret && isHttpUrl(ret)) {
    fragment += `&return=${encodeURIComponent(ret)}`;
  }
  return fragment;
}

/**
 * Decode an `#import=…` fragment back to `{ mime, sourceId?, return? }`, or
 * `null` when the hash isn't a v1 import fragment / fails to decompress.
 *
 * Async because gunzip runs through `DecompressionStream`. Delegates the
 * payload decompression (and the zip-bomb cap) to `decodePermalinkDetailed`
 * by re-framing `&eml=<b64>` back into the `#eml=1:<b64>` shape the permalink
 * decoder expects.
 */
export async function decodeHandoffFragment(
  hash: string,
): Promise<DecodedHandoff | null> {
  if (!hash || !hash.startsWith(FRAGMENT_PREFIX)) return null;
  // Body after `#import=1` — a leading `&` then `key=value&key=value`.
  const body = hash.slice(FRAGMENT_PREFIX.length).replace(/^&/, "");
  const segments = new Map<string, string>();
  for (const seg of body.split("&")) {
    if (!seg) continue;
    const eq = seg.indexOf("=");
    if (eq === -1) continue;
    segments.set(seg.slice(0, eq), seg.slice(eq + 1));
  }
  const eml = segments.get("eml");
  if (!eml) return null;
  const result: PermalinkDecodeResult = await decodePermalinkDetailed(`#eml=1:${eml}`);
  if (!result.ok) return null;
  const out: DecodedHandoff = { mime: result.source };
  const rawSource = segments.get("source");
  if (rawSource && SOURCE_ID_RE.test(rawSource)) out.sourceId = rawSource;
  const rawReturn = segments.get("return");
  if (rawReturn) {
    try {
      const decoded = decodeURIComponent(rawReturn);
      if (isHttpUrl(decoded)) out.return = decoded;
    } catch {
      /* malformed percent-encoding — drop the return link, keep the message */
    }
  }
  return out;
}

/**
 * Strict runtime type guard for a postMessage payload. A vendor posts an
 * arbitrary object across the origin boundary; we trust *nothing* about its
 * shape until this guard passes.
 *
 * Validates: `v === 1`, `kind === "import-eml"`, `mime` is a non-empty string
 * OR a non-empty ArrayBuffer, and (when present) `sourceId` matches the
 * URL-safe id alphabet and `return` is an http(s) URL. Anything else is
 * rejected so a malformed message is dropped before it reaches the parser.
 *
 * SHAPE ONLY. This says the object is well-formed, never that the sender may
 * send it. A hostile page can produce a payload that passes every check
 * above. Whoever adds the `message` listener owns the origin check, and must
 * do it against `event.origin` before calling this, not after.
 */
export function isHandoffMessage(x: unknown): x is HandoffMessage {
  if (typeof x !== "object" || x === null) return false;
  const m = x as Record<string, unknown>;
  if (m.v !== HANDOFF_VERSION) return false;
  if (m.kind !== "import-eml") return false;
  const mime = m.mime;
  const mimeOk =
    (typeof mime === "string" && mime.length > 0) ||
    (mime instanceof ArrayBuffer && mime.byteLength > 0);
  if (!mimeOk) return false;
  if (m.sourceId !== undefined) {
    if (typeof m.sourceId !== "string" || !SOURCE_ID_RE.test(m.sourceId)) {
      return false;
    }
  }
  if (m.return !== undefined) {
    if (typeof m.return !== "string" || !isHttpUrl(m.return)) return false;
  }
  return true;
}

/** True only for an absolute http(s) URL. Used to gate the `return` link. */
function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}
